import { ColorTokens } from '../color-tokens';
import { FontFamily, ThemeConfig, ThemeVariables } from '../theme-types';

const colorTokens: ColorTokens = {
  // Brand colors
  'colors-brand-green-1': '#E6F7F6',
  'colors-brand-green-2': '#C2EDEA',
  'colors-brand-green-3': '#91DEDA',
  'colors-brand-green-4': '#5ECEC9',
  'colors-brand-green-5': '#2EC0B8',
  'colors-brand-green-6': '#00B2A9',
  'colors-brand-green-7': '#009790',
  'colors-brand-green-8': '#007E78',
  'colors-brand-green-9': '#006560',
  'colors-brand-green-10': '#00504C',
  /* Destructive colors */
  'colors-destructive-red-1': '#feeceb',
  'colors-destructive-red-2': '#fbd2cf',
  'colors-destructive-red-3': '#f9afa9',
  'colors-destructive-red-4': '#f68982',
  'colors-destructive-red-5': '#f3665c',
  'colors-destructive-red-6': '#f04438',
  'colors-destructive-red-7': '#cc3a30',
  'colors-destructive-red-8': '#aa3028',
  'colors-destructive-red-9': '#892720',
  'colors-destructive-red-10': '#6c1f19',

  /* Success colors */
  'colors-success-green-1': '#e7f8f0',
  'colors-success-green-2': '#c6eedb',
  'colors-success-green-3': '#99e0bf',
  'colors-success-green-4': '#6ad2a1',
  'colors-success-green-5': '#3dc485',
  'colors-success-green-6': '#12b76a',
  'colors-success-green-7': '#0f9c5a',
  'colors-success-green-8': '#0d824b',
  'colors-success-green-9': '#0a683c',
  'colors-success-green-10': '#085230',

  /* Neutral colors */
  'colors-neutral-1': '#fff',
  'colors-neutral-2': '#fcfcfc',
  'colors-neutral-3': '#f5f5f5',
  'colors-neutral-4': '#f0f0f0',
  'colors-neutral-5': '#d9d9d9',
  'colors-neutral-6': '#bfbfbf',
  'colors-neutral-7': '#8c8c8c',
  'colors-neutral-8': '#595959',
  'colors-neutral-9': '#454545',
  'colors-neutral-10': '#262626',
  'colors-neutral-11': '#1f1f1f',
  'colors-neutral-12': '#141414',
  'colors-neutral-13': '#000',

  /* Warning colors */
  'colors-warning-orange-1': '#fef4e6',
  'colors-warning-orange-2': '#fde4c4',
  'colors-warning-orange-3': '#fccf95',
  'colors-warning-orange-4': '#fab964',
  'colors-warning-orange-5': '#f8a435',
  'colors-warning-orange-6': '#f79009',
  'colors-warning-orange-7': '#d27a08',
  'colors-warning-orange-8': '#af6606',
  'colors-warning-orange-9': '#8d5205',
  'colors-warning-orange-10': '#6f4104',

  /* Informative colors */
  'colors-informative-blue-1': '#e7f6fd',
  'colors-informative-blue-2': '#c4e9fa',
  'colors-informative-blue-3': '#96d8f7',
  'colors-informative-blue-4': '#65c6f3',
  'colors-informative-blue-5': '#37b5ef',
  'colors-informative-blue-6': '#0ba5ec',
  'colors-informative-blue-7': '#098cc9',
  'colors-informative-blue-8': '#0875a8',
  'colors-informative-blue-9': '#065e87',
  'colors-informative-blue-10': '#054a6a',

  /* Support colors - Violet */
  'colors-support-violet-1': '#fbe9f3',
  'colors-support-violet-2': '#f5c9e2',
  'colors-support-violet-3': '#ed9fcb',
  'colors-support-violet-4': '#e473b2',
  'colors-support-violet-5': '#dc489b',
  'colors-support-violet-6': '#d42085',
  'colors-support-violet-7': '#b41b71',
  'colors-support-violet-8': '#97175e',
  'colors-support-violet-9': '#79124c',
  'colors-support-violet-10': '#5f0e3c',

  /* Support colors - Blue */
  'colors-support-blue-1': '#e8f6fb',
  'colors-support-blue-2': '#c7e9f6',
  'colors-support-blue-3': '#9bd7ee',
  'colors-support-blue-4': '#6cc4e6',
  'colors-support-blue-5': '#40b3df',
  'colors-support-blue-6': '#16a2d8',
  'colors-support-blue-7': '#138ab8',
  'colors-support-blue-8': '#107399',
  'colors-support-blue-9': '#0d5c7b',
  'colors-support-blue-10': '#0a4961',

  /* Support colors - Yellow */
  'colors-support-yellow-1': '#fffde6',
  'colors-support-yellow-2': '#fff9c2',
  'colors-support-yellow-3': '#fff591',
  'colors-support-yellow-4': '#fff15e',
  'colors-support-yellow-5': '#ffec2e',
  'colors-support-yellow-6': '#ffe800',
  'colors-support-yellow-7': '#d9c500',
  'colors-support-yellow-8': '#b5a500',
  'colors-support-yellow-9': '#918400',
  'colors-support-yellow-10': '#736800',
};

export const variables: ThemeVariables = {
  'background-gradient':
    'linear-gradient(135.05deg, #26a69a 1.74%, #004d40 100%)',
  'color-error': '#f14643',
  'color-primary-1': '#e0f2f1',
  'color-primary-dark': '#006357',
  'color-primary': '#009c8f',
  'color-secondary-1': '#fce4ec',
  'color-secondary-dark': '#960e44',
  'color-secondary': '#d5216b',
  'color-tertiary-1': '#f2f7fd',
  'color-tertiary': '#3a7ccd',
  'color-tertiary-dark': '#1f508e',
  'color-quaternary': '#fbea00',
  'font-color-highlight': 'var(--color-primary)',
  'font-color-primary': '#3a474e',
  'font-color-secondary': '#6e7881',
  'font-family-body': 'Niramit',
  'font-family-titles': 'Poppins',
  'plan-card-font-color': '#424242',
  'footer-background': 'var(--color-primary)',
  'nav-bar-items-color': '#424242',
  ...colorTokens,
};

const fontFamilies: FontFamily[] = [
  {
    name: 'Niramit',
    axes: 'wght@400;700',
  },
  {
    name: 'Poppins',
    axes: 'wght@400;500;700;800',
  },
];

export const config: ThemeConfig = {
  font: {
    type: 'google-font',
    fontFamilies,
  },
};
